/**
 * ComparisonPresetToolbar Component
 *
 * Renders a toolbar with preset buttons for quick comparison selection.
 * Supports strategies: active_now, active_on, active_latest, active_prior, pick.
 *
 * The "On" button includes a DatePicker dropdown for configurable date lookup.
 */

import React, { useState, useCallback } from 'react'
import { Button, DatePicker, Dropdown, Space, Tooltip, Spin, Alert } from 'antd'
import {
  CheckCircleOutlined,
  CalendarOutlined,
  HistoryOutlined,
  RollbackOutlined,
  SearchOutlined,
  LoadingOutlined,
  DownOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import type { ComparisonPreset, ComparisonPresetsConfig, ComparisonStrategy } from './types'

/**
 * Get the icon component for a preset.
 */
function getPresetIcon(iconName?: string, strategy?: ComparisonStrategy): React.ReactNode {
  // Use provided icon name or fall back to strategy-based default
  const name = iconName || getDefaultIconName(strategy)

  switch (name) {
    case 'CheckCircleOutlined':
      return <CheckCircleOutlined />
    case 'CalendarOutlined':
      return <CalendarOutlined />
    case 'HistoryOutlined':
      return <HistoryOutlined />
    case 'RollbackOutlined':
      return <RollbackOutlined />
    case 'SearchOutlined':
      return <SearchOutlined />
    default:
      return null
  }
}

/**
 * Get default icon name for a strategy.
 */
function getDefaultIconName(strategy?: ComparisonStrategy): string {
  switch (strategy) {
    case 'active_now':
      return 'CheckCircleOutlined'
    case 'active_on':
      return 'CalendarOutlined'
    case 'active_latest':
      return 'HistoryOutlined'
    case 'active_prior':
      return 'RollbackOutlined'
    case 'pick':
      return 'SearchOutlined'
    default:
      return ''
  }
}

/**
 * Default tooltip descriptions for strategies.
 */
function getDefaultDescription(strategy: ComparisonStrategy): string {
  switch (strategy) {
    case 'active_now':
      return 'Compare with what\'s live right NOW (currently effective active version)'
    case 'active_on':
      return 'Compare with what will be active at this date (click to change)'
    case 'active_latest':
      return 'Compare with the newest scheduled pricing (max effectiveFrom)'
    case 'active_prior':
      return 'Compare with what just ended (most recent effectiveTo before now)'
    case 'pick':
      return 'Choose any record to compare'
    default:
      return ''
  }
}

/**
 * Default presets when none configured in metadata.
 */
const DEFAULT_PRESETS: ComparisonPreset[] = [
  { key: 'active_now', label: 'Now', strategy: 'active_now' },
  { key: 'active_prior', label: 'Prior', strategy: 'active_prior' },
  { key: 'pick', label: 'Pick', strategy: 'pick' },
]

interface ComparisonPresetToolbarProps {
  /** Presets configuration from metadata */
  presets?: ComparisonPresetsConfig
  /** Currently active preset key */
  activePreset: string | null
  /** Loading state for preset execution */
  isLoading: boolean
  /** Error message from failed preset execution */
  error: string | null
  /** Current lookup date for active_on strategy */
  lookupDate: string | null
  /** Anchor record's effectiveFrom for date default */
  anchorEffectiveFrom?: string | null
  /** Callback when a preset is selected */
  onPresetSelect: (presetKey: string, date?: string) => void
  /** Callback when lookup date changes */
  onDateChange: (date: string | null) => void
  /** Callback to clear error */
  onClearError?: () => void
  /** Compact mode for mobile displays */
  compact?: boolean
}

/**
 * Toolbar component for comparison preset selection.
 *
 * @example
 * ```tsx
 * <ComparisonPresetToolbar
 *   presets={comparisonConfig.presets}
 *   activePreset={activePreset}
 *   isLoading={isLoadingPreset}
 *   error={presetError}
 *   lookupDate={lookupDate}
 *   onPresetSelect={executePreset}
 *   onDateChange={setLookupDate}
 * />
 * ```
 */
export function ComparisonPresetToolbar({
  presets,
  activePreset,
  isLoading,
  error,
  lookupDate,
  anchorEffectiveFrom,
  onPresetSelect,
  onDateChange,
  onClearError,
  compact = false,
}: ComparisonPresetToolbarProps): JSX.Element {
  const [datePickerOpen, setDatePickerOpen] = useState(false)

  // Button size based on compact mode
  const buttonSize = compact ? 'small' : 'middle'

  // Use configured presets or defaults
  const presetOptions = presets?.options?.length ? presets.options : DEFAULT_PRESETS

  // Handle preset button click
  const handlePresetClick = useCallback(
    (preset: ComparisonPreset) => {
      if (preset.strategy === 'active_on') {
        // For active_on, use current lookupDate or anchor's effectiveFrom
        const date = lookupDate || anchorEffectiveFrom || null
        onPresetSelect(preset.key, date || undefined)
      } else {
        onPresetSelect(preset.key)
      }
    },
    [lookupDate, anchorEffectiveFrom, onPresetSelect]
  )

  // Handle date change for active_on
  const handleDateChange = useCallback(
    (date: Dayjs | null) => {
      const isoDate = date ? date.toISOString() : null
      onDateChange(isoDate)

      // If active_on is already selected, re-execute with new date
      const activeOnPreset = presetOptions.find((p) => p.strategy === 'active_on')
      if (activeOnPreset && activePreset === activeOnPreset.key && isoDate) {
        onPresetSelect(activeOnPreset.key, isoDate)
      }

      setDatePickerOpen(false)
    },
    [onDateChange, onPresetSelect, activePreset, presetOptions]
  )

  // Format date for display
  const formatDateLabel = (date: string | null, effectiveFrom?: string | null): string => {
    const displayDate = date || effectiveFrom
    if (!displayDate) return 'On'

    const d = dayjs(displayDate)
    if (!d.isValid()) return 'On'

    // Use short format for button: "On: Jan 5"
    return `On: ${d.format('MMM D')}`
  }

  // Render a single preset button
  const renderPresetButton = (preset: ComparisonPreset) => {
    const isActive = activePreset === preset.key
    const isThisLoading = isLoading && isActive
    const description = preset.description || getDefaultDescription(preset.strategy)

    // Special handling for active_on with date picker
    if (preset.strategy === 'active_on') {
      const dateLabel = formatDateLabel(lookupDate, anchorEffectiveFrom)
      const currentDate = lookupDate || anchorEffectiveFrom
      const dayjsValue = currentDate ? dayjs(currentDate) : null

      const dropdownContent = (
        <div style={{ padding: 8, background: 'var(--rp-surface-primary)', borderRadius: 4 }}>
          <DatePicker
            value={dayjsValue}
            onChange={handleDateChange}
            open={datePickerOpen}
            onOpenChange={setDatePickerOpen}
            style={{ width: '100%' }}
            allowClear={false}
          />
        </div>
      )

      return (
        <Tooltip key={preset.key} title={description}>
          <Dropdown
            popupRender={() => dropdownContent}
            trigger={['click']}
            open={datePickerOpen}
            onOpenChange={setDatePickerOpen}
          >
            <Button
              type={isActive ? 'primary' : 'default'}
              size={buttonSize}
              icon={isThisLoading ? <LoadingOutlined spin /> : getPresetIcon(preset.icon, preset.strategy)}
              onClick={(e) => {
                e.stopPropagation()
                handlePresetClick(preset)
              }}
            >
              {compact ? dateLabel.replace('On: ', '') : dateLabel}
              <DownOutlined style={{ fontSize: compact ? 8 : 10, marginLeft: compact ? 2 : 4 }} />
            </Button>
          </Dropdown>
        </Tooltip>
      )
    }

    // Standard preset button
    return (
      <Tooltip key={preset.key} title={description}>
        <Button
          type={isActive ? 'primary' : 'default'}
          size={buttonSize}
          icon={isThisLoading ? <LoadingOutlined spin /> : getPresetIcon(preset.icon, preset.strategy)}
          onClick={() => handlePresetClick(preset)}
        >
          {preset.label}
        </Button>
      </Tooltip>
    )
  }

  return (
    <div className="comparison-preset-toolbar">
      <Space size="small" wrap>
        {presetOptions.map(renderPresetButton)}
        {isLoading && !activePreset && (
          <Spin size="small" style={{ marginLeft: 8 }} />
        )}
      </Space>

      {error && (
        <Alert
          type="warning"
          message={error}
          closable
          onClose={onClearError}
          style={{ marginTop: 8 }}
          showIcon
        />
      )}
    </div>
  )
}

export default ComparisonPresetToolbar
